/**
 * Preise-Reiter: laufende Übertragungen, Kalender der nächsten 90 Tage und die zuletzt an Channex
 * gesendeten Zeiträume. Daten in `usePreiseTabData`, Kopf mit den Übertragungswegen in
 * `PreiseTabHeader`.
 */
import { useState } from 'react';
import { Info } from 'lucide-react';
import { EmptyState } from '@/shared/components';
import type { AriStateDay } from '@/shared/api';
import { PRIVILEGES } from '@stayon/auth-core';
import { can, useAuth } from '@/shared/auth';
import type { AriInitialValues } from '../ari-edit/helpers';
import { AriEditModal, MultiRangePushModal } from '../ari-edit';
import { AriOperationStatusPanel } from '../ari-edit/AriOperationStatusPanel';
import { PreiseTabHeader } from '../preise/PreiseTabHeader';
import { PushHistoryCard } from '../preise/PushHistoryCard';
import { reapplyValues } from '../preise/pushHistoryPresentation';
import { usePreiseTabData } from '../preise/usePreiseTabData';
import { PriceCalendar } from '../preise/PriceCalendar';
import { PriceCalendarLoadState } from '../preise/PriceCalendarLoadState';
import { useApartmentDetail } from './ApartmentDetailLayout';

export function PreiseTab() {
  const { propertyId: id } = useApartmentDetail();
  const { user } = useAuth();
  const canEdit = can(user, PRIVILEGES.availabilityDistributionEdit);
  const canFull = can(user, PRIVILEGES.availabilityDistributionFull);

  const [modalOpen, setModalOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [initial, setInitial] = useState<AriInitialValues | undefined>();

  const { property, bookingsByDate, ariDays, history, calendarQuery } = usePreiseTabData(id);

  const openModal = (init?: AriInitialValues) => {
    setInitial(init);
    setModalOpen(true);
  };

  const onDayClick = (date: string, state: AriStateDay | null) => {
    openModal({
      from: date,
      to: date,
      rate: state?.rate ?? null,
      // Verfügbarkeit den echten Tageszustand spiegeln (0/1 → blockiert/verfügbar).
      available: state?.availability == null ? null : state.availability > 0,
      minStay: state?.minStay ?? null,
      maxStay: state?.maxStay ?? null,
      // false = Channex-Default „keine Sperre" → als unverändert behandeln, sonst würden
      // ausgeschaltete Restriktionen (CTA/CTD/Stop-Sell) bei jedem Edit ungewollt mitgepusht.
      // An-/Abreisesperre nur anzeigen: vorbelegt ginge eine gespeicherte Sperre bei einer reinen
      // Preisänderung über einen breiteren Zeitraum mit raus.
      currentRestrictions: {
        closedToArrival: state?.closedToArrival ?? null,
        closedToDeparture: state?.closedToDeparture ?? null,
      },
      stopSell: state?.stopSell || null,
    });
  };

  if (!property) return null;
  // Bearbeiten öffnet dieselbe Maske wie ein Klick in den Kalender; ohne Recht fehlt sie ganz.
  const canReapply = canEdit && Boolean(property.channexPropertyId);

  return (
    <>
      <PreiseTabHeader
        published={property.channexUnitPublished}
        currency={property.currency}
        propertyId={id}
        canEdit={canEdit}
        canFull={canFull}
        onOpenBatch={() => setBatchOpen(true)}
        onOpenEditor={() => openModal()}
      />

      {!property.channexUnitPublished ? (
        <EmptyState
          icon="Webhook"
          title="Dieses Apartment ist beim Kanalmanager noch nicht veröffentlicht"
          description="Preise lassen sich erst übertragen, wenn dieses Apartment über die Veröffentlichung des Objekts live ist — ein veröffentlichtes Objekt allein genügt nicht."
        />
      ) : (
        <div className="space-y-6">
          <AriOperationStatusPanel propertyId={id} />
          {property.baseNightlyRate <= 0 && (
            <div className="border-line/50 bg-card-alt/60 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm">
              <Info size={16} strokeWidth={2} className="text-sealwax mt-0.5 shrink-0" />
              <div className="text-ink-soft flex-1">
                <span className="text-ink font-medium">Es fehlt ein Standardpreis.</span> Tragen Sie
                ihn in den Stammdaten unter „An die Buchungsportale" ein — er gilt für jeden Tag,
                für den kein eigener Preis übertragen wurde.
              </div>
            </div>
          )}

          <div className="bg-card border-line/50 shadow-card rounded-lg border p-6">
            <div className="mb-6">
              <div className="text-ink-soft text-[11px] font-semibold uppercase tracking-[0.12em]">
                An die Buchungsportale
              </div>
              <p className="text-ink-faint mt-1 text-xs">
                Änderungen werden sicher vorgemerkt und nach der Channex-Rückprüfung bestätigt.
              </p>
            </div>
            {calendarQuery.isError || calendarQuery.isLoading ? (
              <PriceCalendarLoadState query={calendarQuery} />
            ) : (
              <PriceCalendar
                bookingCellsByDate={bookingsByDate}
                ariDays={ariDays}
                defaultRate={property.baseNightlyRate}
                currency={property.currency}
                onDayClick={canEdit ? onDayClick : undefined}
              />
            )}
          </div>
          <PushHistoryCard
            history={history}
            currency={property.currency}
            onReapply={canReapply ? (item) => openModal(reapplyValues(item)) : undefined}
          />
        </div>
      )}

      {property.channexPropertyId && canEdit && (
        <>
          <AriEditModal
            open={modalOpen}
            onOpenChange={setModalOpen}
            propertyId={id}
            currency={property.currency}
            initial={initial}
          />
          <MultiRangePushModal open={batchOpen} onOpenChange={setBatchOpen} propertyId={id} />
        </>
      )}
    </>
  );
}
